import { useRef, useState, type FunctionComponent, type SyntheticEvent } from 'react';
import { useRecurly } from '@recurly/react-recurly';
import { Button } from '@fff-web/fff-ui-shared';
import { ECardType, IUpgradeForm } from '../../../interfaces';
import CreditCardForm from '../../../lib/Components/Recurly/Forms/CreditCardForm';
import { getCardExpiration, getCardLogo } from '../../../lib/helpers';
import useCreditCardFormState from '../../../lib/hooks/useCreditCardFormState';
import { CTA_UPDATE } from '../../../lib/utils/buttonCTA';
import * as styles from './UpgradeForm.module.css';

const UpgradeForm: FunctionComponent<IUpgradeForm> = ({
  onSubmit = () => null,
  cardInformation = undefined,
  isDisabled = false,
}) => {
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const recurly = useRecurly();
  const cardType =
    cardInformation &&
    ((cardInformation.brand || cardInformation.cardType) as keyof typeof ECardType);
  const cardLogo = cardType && getCardLogo({ cardType });
  const { fields: fieldsState, onResponse } = useCreditCardFormState(false);

  function handleSubmit(event: SyntheticEvent): void {
    event.preventDefault();
    setIsSendingRequest(true);
    formRef?.current &&
      recurly.token(formRef.current, (err, token) => {
        if (err) {
          onResponse(err, token);
        } else {
          onSubmit(token);
        }
        setIsSendingRequest(false);
      });
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef} className={styles.ufContainer}>
      <p className={styles.ufTitle}>Billing Information</p>
      <p className={styles.ufCard}>
        {cardInformation && (
          <>
            {cardLogo && <img src={cardLogo} alt="Card" />}
            <span className={styles.lastFour}>{`•••• ${cardInformation.lastFour},`}</span>
            {`exp ${getCardExpiration(cardInformation)}`}
            <input type="hidden" data-recurly="country" value={cardInformation.country} />
          </>
        )}
      </p>
      <CreditCardForm
        name={fieldsState.first_name}
        lastName={fieldsState.last_name}
        number={fieldsState.number}
        month={fieldsState.month}
        year={fieldsState.year}
        cvv={fieldsState.cvv}
        postalCode={fieldsState.postal_code}
      />
      <Button
        type="submit"
        styles={{ alignSelf: 'center', margin: '36px 0 36px 0' }}
        disabled={
          isDisabled ||
          isSendingRequest ||
          !fieldsState.first_name?.value ||
          !fieldsState.last_name?.value
        }
        loading={isSendingRequest}
      >
        {CTA_UPDATE}
      </Button>
    </form>
  );
};

export default UpgradeForm;
